import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, MessageCircle, ExternalLink } from "lucide-react";
import { COMPANY, getPhoneLink, getEmailLink, getWhatsAppLink, getMapsLink } from "@/config/company";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

interface ContactInfoCardProps {
  type: "phone" | "whatsapp" | "email" | "location";
  title: string;
  subtitle: string;
  value: string;
  actionLabel?: string;
}

export function ContactInfoCard({ type, title, subtitle, value, actionLabel }: ContactInfoCardProps) {
  const { trackPhoneClick, trackWhatsAppClick, trackCTAClick } = useAnalyticsContext();
  const getIcon = () => {
    switch (type) {
      case "phone":
        return <Phone className="w-6 h-6 text-primary" />;
      case "whatsapp":
        return <MessageCircle className="w-6 h-6 text-white" />;
      case "email":
        return <Mail className="w-6 h-6 text-primary" />;
      case "location":
        return <MapPin className="w-6 h-6 text-accent" />;
    }
  };

  const getActionLink = () => {
    switch (type) {
      case "phone":
        return getPhoneLink();
      case "whatsapp":
        return getWhatsAppLink();
      case "email":
        return getEmailLink("info");
      case "location":
        return getMapsLink();
    }
  };

  const getActionButtonProps = () => {
    switch (type) {
      case "phone":
        return {
          variant: "outline" as const,
          className: "gap-1.5 hover:bg-primary hover:text-primary-foreground",
          label: "Call Now",
          icon: <Phone className="w-3.5 h-3.5" />,
        };
      case "whatsapp":
        return {
          variant: "default" as const,
          className: "bg-[#25D366] hover:bg-[#128C7E] text-white gap-1.5",
          label: "Chat on WhatsApp",
          icon: <MessageCircle className="w-3.5 h-3.5" />,
        };
      case "email":
        return {
          variant: "outline" as const,
          className: "gap-1.5 hover:bg-primary hover:text-primary-foreground",
          label: "Send Email",
          icon: <Mail className="w-3.5 h-3.5" />,
        };
      case "location":
        return {
          variant: "outline" as const,
          className: "gap-1.5 hover:bg-accent hover:text-accent-foreground",
          label: "View on Maps",
          icon: <ExternalLink className="w-3.5 h-3.5" />,
        };
    }
  };

  const buttonProps = getActionButtonProps();
  const isWhatsApp = type === "whatsapp";

  const handleClick = () => {
    switch (type) {
      case "phone":
        trackPhoneClick('contact_page');
        break;
      case "whatsapp":
        trackWhatsAppClick('contact_page');
        break;
      case "email":
        trackCTAClick('Send Email', 'contact_page', 'email');
        break;
      case "location":
        trackCTAClick('View on Maps', 'contact_page', 'location');
        break;
    }
  };

  return (
    <Card className={`border-0 shadow-soft ${isWhatsApp ? "bg-[#25D366]/10" : ""}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              isWhatsApp
                ? "bg-[#25D366]"
                : type === "location"
                ? "bg-accent/10"
                : "bg-primary/10"
            }`}
          >
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-secondary mb-1">{title}</h3>
            <p className="text-muted-foreground text-sm mb-2">{subtitle}</p>
            {type === "location" ? (
              <p className="text-muted-foreground text-sm whitespace-pre-line">{value}</p>
            ) : (
              <p className="font-medium text-primary truncate">{value}</p>
            )}
            <div className="mt-3">
              <Button
                asChild
                size="sm"
                variant={buttonProps.variant}
                className={buttonProps.className}
              >
                <a
                  href={getActionLink()}
                  target={type === "location" || type === "whatsapp" ? "_blank" : undefined}
                  rel={type === "location" || type === "whatsapp" ? "noopener noreferrer" : undefined}
                  onClick={handleClick}
                >
                  {buttonProps.icon}
                  {actionLabel || buttonProps.label}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
